import {useMutation, useQueryClient} from "@tanstack/react-query";
import React, {useEffect, useRef, useState} from "react";
import {flushSync} from "react-dom";
import {FormattedMessage} from "react-intl";
import {RatingIndications, RatingRequest} from "../model/rideInfo";
import {closeModal, openModal} from "../utils/dialog";
import "../styles/Dialog.css";
import {ErrorAlert} from "./ErrorAlert";
import "../styles/Feedback.css";
import {Indications} from "./Indications";
import {StarRating} from "./StarRating";
import {TextArea} from "./TextArea";

function getRatingTitle(rating: number) {
    switch (rating) {
        case 1:
        case 2:
        case 3:
            return (
                <>
                    <p>
                        <strong>
                            <FormattedMessage id="feedback_negative_title" />
                        </strong>
                    </p>
                    <p style={{fontSize: "14px"}}>
                        <FormattedMessage id="feedback_subtitle" />
                    </p>
                </>
            );
        case 4:
            return (
                <>
                    <p>
                        <strong>
                            <FormattedMessage id="feedback_neutral_title" />
                        </strong>
                    </p>
                    <p style={{fontSize: "14px"}}>
                        <FormattedMessage id="feedback_subtitle" />
                    </p>
                </>
            );
        case 5:
            return (
                <p>
                    <strong>
                        <FormattedMessage id="feedback_positive_title" />
                    </strong>
                </p>
            );
        default:
            return null;
    }
}

export function Feedback({
    shareKey,
    givenRating,
    title,
    indications,
    rateFunction,
}: {
    shareKey: string;
    givenRating: number | undefined;
    title: string;
    indications: RatingIndications | undefined;
    rateFunction: (id: string, rating: RatingRequest) => Promise<any>;
}) {
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState<string | undefined>(undefined);
    const [selectedIndications, setSelectedIndications] = useState<string[] | undefined>(undefined);
    const [feedbackMandatory, setFeedbackMandatory] = useState(false);
    const dialogRef = useRef<HTMLDialogElement>(null);
    const commentRef = useRef<HTMLTextAreaElement>(null);
    const queryClient = useQueryClient();

    const rate = useMutation({mutationFn: (body: RatingRequest) => rateFunction(shareKey, body)});

    const isNegativeRating = rating < 5;

    useEffect(() => {
        if (rating > 0) {
            openModal(dialogRef.current);
        }
    }, [rating]);

    function handleSubmit() {
        if (isNegativeRating && !Boolean(comment) && !selectedIndications) {
            setFeedbackMandatory(true);
            return;
        }
        rate.mutate(
            {
                rating,
                comment,
                positiveIndications: isNegativeRating ? undefined : selectedIndications,
                negativeIndications: isNegativeRating ? selectedIndications : undefined,
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries();
                    closeModal(dialogRef.current);
                },
            }
        );
    }

    function handleCommentSelected() {
        flushSync(() => {
            setComment("");
        });
        commentRef.current?.focus();
        commentRef.current?.scrollIntoView();
    }

    const starRating = <StarRating {...{rating, setRating}} />;
    if (givenRating) {
        return (
            <div
                style={{display: "flex", justifyContent: "space-between", flexWrap: "wrap"}}
                data-cy="given-rating-field"
            >
                <FormattedMessage id="thank_you_for_rating" />
                <div>
                    <span style={{color: "var(--brand)"}}>{"★ ".repeat(givenRating)}</span>
                    <span style={{color: "var(--tertiary)"}}>{"★ ".repeat(5 - givenRating)}</span>
                </div>
            </div>
        );
    }
    return (
        <>
            <h3>{title}</h3>
            {starRating}
            {rating > 0 && (
                <dialog className="modal" ref={dialogRef}>
                    <div className="header" style={{position: "sticky"}}>
                        <p id="title" className="title">
                            {title}
                        </p>
                        <button
                            data-cy="close-rating-modal-btn"
                            className="close-button"
                            onClick={() => {
                                setRating(0);
                                setSelectedIndications(undefined);
                                closeModal(dialogRef.current);
                            }}
                        >
                            ×
                        </button>
                    </div>
                    {starRating}
                    {indications && (
                        <>
                            <div style={{marginTop: "8px"}}>{getRatingTitle(rating)}</div>
                            <Indications
                                indications={
                                    isNegativeRating ? indications.negative : indications.positive
                                }
                                selectedIndications={selectedIndications}
                                setSelectedIndications={setSelectedIndications}
                                commentSelected={comment != null}
                                handleCommentSelected={handleCommentSelected}
                            />
                        </>
                    )}
                    {comment != null && (
                        <>
                            <p style={{marginBottom: "8px"}}>
                                <strong>
                                    <FormattedMessage id="comment" />
                                </strong>
                            </p>
                            <TextArea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                ref={commentRef}
                            />
                        </>
                    )}
                    {isNegativeRating && (
                        <span
                            style={{
                                color: "var(--secondary)",
                                fontSize: "12px",
                                textAlign: "center",
                                width: "100%",
                            }}
                        >
                            <FormattedMessage id="feedback_customer_service_info" />
                        </span>
                    )}
                    {rate.isError && (
                        <ErrorAlert>
                            <FormattedMessage id="rating_failed" />
                        </ErrorAlert>
                    )}
                    {feedbackMandatory && (
                        <ErrorAlert>
                            <FormattedMessage id="please_select_a_reason" />
                        </ErrorAlert>
                    )}
                    <button
                        data-cy="send-rating-btn"
                        className="primary-button"
                        onClick={handleSubmit}
                        disabled={rate.isPending}
                    >
                        {rate.isPending ? (
                            <FormattedMessage id="sending" />
                        ) : (
                            <FormattedMessage id="send_rating" />
                        )}
                    </button>
                </dialog>
            )}
        </>
    );
}
